
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, AlertTriangle, CheckCircle, FileText, Download, Plus } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ComplianceItem {
  id: string;
  title: string;
  category: string;
  status: 'compliant' | 'non-compliant' | 'pending' | 'overdue';
  dueDate: string;
  completionDate?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  description: string;
}

export default function ComplianceTracking() {
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([
    {
      id: '1',
      title: 'GDPR Data Protection Assessment',
      category: 'Data Protection',
      status: 'compliant',
      dueDate: '2024-01-31',
      completionDate: '2024-01-25',
      priority: 'high',
      description: 'Annual GDPR compliance review and assessment'
    },
    {
      id: '2',
      title: 'ISO 27001 Security Audit',
      category: 'Information Security',
      status: 'pending',
      dueDate: '2024-02-15',
      priority: 'critical',
      description: 'Annual information security management system audit'
    },
    {
      id: '3',
      title: 'Financial Compliance Report',
      category: 'Financial',
      status: 'overdue',
      dueDate: '2024-01-20',
      priority: 'high',
      description: 'Quarterly financial compliance and regulatory reporting'
    },
    {
      id: '4',
      title: 'Health & Safety Training',
      category: 'Health & Safety',
      status: 'compliant',
      dueDate: '2024-03-01',
      completionDate: '2024-01-15',
      priority: 'medium',
      description: 'Mandatory health and safety training for all staff'
    }
  ]);

  const [filter, setFilter] = useState({
    status: 'all',
    category: 'all',
    priority: 'all'
  });

  const getStatusColor = (status: ComplianceItem['status']) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'non-compliant': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: ComplianceItem['priority']) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredItems = complianceItems.filter(item => {
    if (filter.status !== 'all' && item.status !== filter.status) return false;
    if (filter.category !== 'all' && item.category !== filter.category) return false;
    if (filter.priority !== 'all' && item.priority !== filter.priority) return false;
    return true;
  });

  const complianceStats = {
    total: complianceItems.length,
    compliant: complianceItems.filter(item => item.status === 'compliant').length,
    pending: complianceItems.filter(item => item.status === 'pending').length,
    overdue: complianceItems.filter(item => item.status === 'overdue').length,
    nonCompliant: complianceItems.filter(item => item.status === 'non-compliant').length
  };

  const complianceRate = (complianceStats.compliant / complianceStats.total) * 100;

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Compliance Tracking
          </h1>
          <p className="text-muted-foreground">
            Monitor and manage regulatory compliance across all project areas
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="requirements">Requirements</TabsTrigger>
            <TabsTrigger value="audits">Audits</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{complianceRate.toFixed(1)}%</div>
                    <p className="text-sm text-muted-foreground">Compliance Rate</p>
                    <Progress value={complianceRate} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{complianceStats.compliant}</div>
                    <p className="text-sm text-muted-foreground">Compliant Items</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{complianceStats.pending}</div>
                    <p className="text-sm text-muted-foreground">Pending Review</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{complianceStats.overdue}</div>
                    <p className="text-sm text-muted-foreground">Overdue Items</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Data Protection', 'Information Security', 'Financial', 'Health & Safety'].map((category) => {
                      const categoryItems = complianceItems.filter(item => item.category === category);
                      const compliantItems = categoryItems.filter(item => item.status === 'compliant');
                      const categoryRate = categoryItems.length > 0 ? (compliantItems.length / categoryItems.length) * 100 : 0;
                      
                      return (
                        <div key={category} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="font-medium">{category}</span>
                            <span>{categoryRate.toFixed(0)}% ({compliantItems.length}/{categoryItems.length})</span>
                          </div>
                          <Progress value={categoryRate} className="w-full" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    High Priority Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complianceItems
                      .filter(item => (item.priority === 'high' || item.priority === 'critical') && (item.status === 'overdue' || item.status === 'pending'))
                      .map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{item.title}</p>
                            <p className="text-xs text-muted-foreground">Due: {new Date(item.dueDate).toLocaleDateString()}</p>
                          </div>
                          <Badge className={getPriorityColor(item.priority)}>
                            {item.priority}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="requirements" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Compliance Requirements</span>
                  <div className="flex gap-2">
                    <Select value={filter.status} onValueChange={(value) => setFilter({...filter, status: value})}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="compliant">Compliant</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="non-compliant">Non-Compliant</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filter.category} onValueChange={(value) => setFilter({...filter, category: value})}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="Data Protection">Data Protection</SelectItem>
                        <SelectItem value="Information Security">Information Security</SelectItem>
                        <SelectItem value="Financial">Financial</SelectItem>
                        <SelectItem value="Health & Safety">Health & Safety</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Requirement
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredItems.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{item.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span>Category: {item.category}</span>
                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                              {item.completionDate && (
                                <span>Completed: {new Date(item.completionDate).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <Badge className={getStatusColor(item.status)}>
                              {item.status}
                            </Badge>
                            <Badge className={getPriorityColor(item.priority)}>
                              {item.priority}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audits" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Audits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">ISO 27001 Annual Audit</h4>
                      <p className="text-sm text-muted-foreground">Information Security Management</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-yellow-100 text-yellow-800">Scheduled</Badge>
                        <span className="text-sm">Feb 15, 2024</span>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">GDPR Compliance Review</h4>
                      <p className="text-sm text-muted-foreground">Data Protection Assessment</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>
                        <span className="text-sm">Mar 1, 2024</span>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Financial Audit Q1</h4>
                      <p className="text-sm text-muted-foreground">Quarterly Financial Review</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-gray-100 text-gray-800">Planned</Badge>
                        <span className="text-sm">Mar 31, 2024</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Audit Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Health & Safety Audit</h4>
                      <p className="text-sm text-muted-foreground">Annual workplace safety review</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-green-100 text-green-800">Passed</Badge>
                        <span className="text-sm">Jan 10, 2024</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Score: 94/100</p>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Quality Management Audit</h4>
                      <p className="text-sm text-muted-foreground">ISO 9001 compliance review</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-green-100 text-green-800">Passed</Badge>
                        <span className="text-sm">Dec 15, 2023</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Score: 89/100</p>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Environmental Audit</h4>
                      <p className="text-sm text-muted-foreground">ISO 14001 environmental management</p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-yellow-100 text-yellow-800">Minor Issues</Badge>
                        <span className="text-sm">Nov 30, 2023</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Score: 78/100</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Compliance Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Compliance Dashboard</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Audit Summary Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Risk Assessment</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Regulatory Updates</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Compliance Trends</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Action Plan Report</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
